import { AppNavigation, AppStepper, TierStepper } from "@zenith/hooks";
import {
  DatepickerComponent,
  ModalComponent,
  SelectComponent,
  TextFieldComponent,
} from "@zenith/components";
import { memo, useState } from "react";
import {
  FormErrorsType,
  NextOfKinDetailsPageProps,
  RelationshipItems,
  SelectedRelationshipError,
} from "./interfaces";
import FlagNigeria from "@zenith/assets/icons/FlagNigeria";
import {
  setDateOfBirthValidations,
  setEmailValidations,
  setFirstNameValidations,
  setLastNameValidations,
  setPhoneNumberValidations,
  setRelationshipValidations,
} from "@zenith/features/Tiers/validations/NextOfKinValidations";
import dayjs from "dayjs";
import {
  DEFAULT_COUNTRY_CODE,
  TRANSACTIONS_DATE_FILTER_FORMAT,
} from "@zenith/data/constants";
import { GreenTick } from "@zenith/assets/icons/components";
import StepStatusComponent from "@zenith/features/Tiers/components/StepStatusComponent/step-status-component/step-status-component";
import { getPhoneNumberLength } from "@zenith/features/globalUtils";
import { MerchantProfileFields } from "@zenith/features/Profile/utils/form-fields";
import {
  ApiResponseDTODiscardUpgradeResponseDTO,
  ApiResponseDTONextOfKinDTO,
  NextOfKinDTO,
  TierApiService,
  TierStepsResponseDTO,
  UsersApiService,
} from "@zenith/services/user";
import { UPGRADE_ACCOUNT } from "@zenith/routes/paths";
import { useUpgradeContext } from "@zenith/context";
import { getMaxDateOfBirth, updateTierStepStatus } from "@zenith/utils/Helpers";
import CheckCircle from "@zenith/assets/icons/CheckCircle";

const NextOfKinDetailsPage = memo(function NextOfKinDetailsPage(
  props: NextOfKinDetailsPageProps
) {
  const { navigateTo } = AppNavigation();
  const { setActiveStep } = AppStepper();
  const { goToNextOpenStep } = TierStepper();
  const { upgradeContext, setUpgradeContext, clearUpgradeContext } =
    useUpgradeContext();

  const [backModal, setBackModal] = useState<boolean>(false),
    [formData, setFormData] = useState<NextOfKinDTO>({
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      emailAddress: "",
      phoneNumber: "",
      relationship: "",
    }),
    [selectedRelationshipError, setSelectedRelantionshipError] =
      useState<SelectedRelationshipError>({ hasError: false, message: "" }),
    [formErrors, setFormErrors] = useState<FormErrorsType>({
      firstName: { message: "", hasError: false },
      lastName: { message: "", hasError: false },
      dateOfBirth: { message: "", hasError: false },
      emailAddress: { message: "", hasError: false },
      phoneNumber: { message: "", hasError: false },
      relationship: { message: "", hasError: false },
    }),
    [initialCalendarOpen, setInitialCalendarOpen] = useState<boolean>(false),
    [isInformationVerified, setIsInformationVerified] = useState<boolean>(
      upgradeContext.isRecoveryFlow as boolean
    ),
    [isContinueDisabled, setIsContinueDisabled] = useState<boolean>(false);
  const relationshipItems: RelationshipItems[] = [
    { value: "Sibling", label: "Sibling" },
    { value: "Parent", label: "Parent" },
    { value: "Spouse", label: "Spouse" },
    { value: "Child", label: "Child" },
    { value: "Extended Family", label: "Extended Family" },
  ];

  const onFieldBlur = (field: keyof NextOfKinDTO) => {
    const formDataField = formData[field];
    if (formDataField === undefined) return;
    handleFormErrors(field, formDataField);
  };

  const handleFormErrors = (field: keyof NextOfKinDTO, value: string) => {
    setFormErrors((prevFormErrors) => ({
      ...prevFormErrors,
      [field]: validateField(field, value),
    }));
  };

  const validateField = (
    field: keyof NextOfKinDTO,
    value: string
  ): { hasError: boolean; message: string } => {
    switch (field) {
      case "firstName":
        return setFirstNameValidations(value);
      case "lastName":
        return setLastNameValidations(value);
      case "dateOfBirth":
        return setDateOfBirthValidations(value);
      case "emailAddress":
        return setEmailValidations(value);
      case "phoneNumber":
        return setPhoneNumberValidations(value);
      case "relationship":
        return setRelationshipValidations(value);
      default:
        console.warn("Field not found");
    }
    return { hasError: value.trim() === "", message: "This field is required" };
  };

  const handleModalState = () => {
    setBackModal(!backModal);
  };

  const handleDiscardUpgrade = () => {
    TierApiService.discardUpgradeTier()
      .then((response: ApiResponseDTODiscardUpgradeResponseDTO) => {
        if ("data" in response && response.data) {
          setActiveStep(0);
          clearUpgradeContext();
          navigateTo(UPGRADE_ACCOUNT);
        }
      })
      .catch((error) => {
        return error;
      });
  };

  const onFieldChange = (
    field: keyof NextOfKinDTO,
    event?: React.ChangeEvent<HTMLInputElement> | unknown,
    value?: string | RelationshipItems[]
  ) => {
    let updatedEventValue = "";

    if (
      event &&
      typeof event === "object" &&
      "target" in event &&
      event.target instanceof HTMLInputElement
    ) {
      updatedEventValue = event.target.value;
    } else if (value) {
      updatedEventValue = value as string;
    }

    switch (field) {
      case "firstName":
      case "lastName":
        updatedEventValue = updatedEventValue.replace(/[^a-zA-Z -]/gi, "");
        break;
      case "emailAddress":
        updatedEventValue = updatedEventValue.replace(/\s/g, "");
        break;
      case "phoneNumber":
        updatedEventValue = updatedEventValue.replace(/[^\d]/g, "");
        break;
      case "dateOfBirth":
        if (event && typeof event === "object" && "$d" in event) {
          updatedEventValue = dayjs(event.$d as string).format(
            TRANSACTIONS_DATE_FILTER_FORMAT
          ) as unknown as string;
          setInitialCalendarOpen(false);
        }
        break;
      case "relationship":
        updatedEventValue = event as string;
        break;
      default:
        console.warn("Field not found");
    }

    setFormData((prevFormData: NextOfKinDTO) => ({
      ...prevFormData,
      [field]: updatedEventValue,
    }));

    setFormErrors((prevFormErrors: FormErrorsType) => ({
      ...prevFormErrors,
      [field]: { message: "", hasError: false },
    }));
  };

  const isFormValid = (): boolean => {
    let isValid = true;
    const isPhoneNumberValid = setPhoneNumberValidations(
      formData.phoneNumber as string
    ).hasError;

    for (const key in formData) {
      if (key === "emailAddress") {
        continue;
      } else if (formData[key as keyof NextOfKinDTO] === "") {
        isValid = false;
      } else if (isPhoneNumberValid) {
        isValid = false;
      }
    }

    for (const key in formErrors) {
      if (formErrors[key as keyof FormErrorsType].hasError) {
        isValid = false;
      }
    }

    return isValid;
  };

  const onSelectBlur = (newValue: string) => {
    if (!newValue) {
      setSelectedRelantionshipError({
        hasError: true,
        message: "Relationship is required",
      });
      return;
    }
    setSelectedRelantionshipError({
      hasError: false,
      message: "",
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    setIsContinueDisabled(true);

    UsersApiService.postNextOfKin(formData)
      .then((response: ApiResponseDTONextOfKinDTO) => {
        if ("data" in response && response.data) {
          setIsInformationVerified(true);

          const tierStepsUpdated: TierStepsResponseDTO[] = updateTierStepStatus(
            upgradeContext,
            TierStepsResponseDTO.stepName.NEXT_OF_KIN_STEP,
            TierStepsResponseDTO.stepStatus.APPROVED // Next of KIN is always approved
          );

          setUpgradeContext({
            ...upgradeContext,
            tierSteps: tierStepsUpdated,
          });
        }
      })
      .catch((error) => {
        return error;
      });
  };

  return (
    <div className="min-w-full">
      <ModalComponent
        body="Are you sure you want to leave?"
        secondLine="All information provided will be lost."
        title="Discard Upgrade"
        showModal={backModal}
        onClickClose={handleModalState}
        doubleOptions={true}
        onLeftButtonClick={handleDiscardUpgrade}
        onRightButtonClick={handleModalState}
        leftButtonText="Discard Upgrade"
        rightButtonText="Return to Upgrade"
      />

      {!isInformationVerified ? (
        <div className="mb-5 w-full overflow-hidden">
          <h5 className="mb-4 text-4xl font-medium">Next Of Kin Details</h5>
          <form onSubmit={handleSubmit} autoComplete="off">
            <div className="mb-5 overflow-hidden">
              <div className="mb-3 min-w-0 text-base text-neutral-600">
                Provide your next of Kin Details
              </div>
            </div>
            <div className="mt-3 flex flex-col gap-y-7">
              <div>
                <TextFieldComponent
                  id="firstName"
                  label="First Name"
                  labelId="firstNameLabel"
                  type="text"
                  placeholder="Enter first name"
                  value={formData.firstName || ""}
                  hasError={false}
                  onChange={(e) => onFieldChange("firstName", e)}
                  onBlur={() => onFieldBlur("firstName")}
                  maxLength={25}
                />

                <div className="mt-1 ml-2 text-xs text-red-800">
                  <span className="break-words">
                    {formErrors.firstName.message}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-3 flex flex-col gap-y-7">
              <div>
                <TextFieldComponent
                  id="lastName"
                  label="Last Name"
                  labelId="lastNameLabel"
                  type="text"
                  placeholder="Enter last name"
                  value={formData.lastName || ""}
                  hasError={false}
                  onChange={(e) => onFieldChange("lastName", e)}
                  onBlur={() => onFieldBlur("lastName")}
                  maxLength={25}
                />

                <div className="mt-1 ml-2 text-xs text-red-800">
                  <span className="break-words">
                    {formErrors.lastName.message}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-3 flex flex-col gap-y-7">
              <div>
                <DatepickerComponent
                  onOpen={() => setInitialCalendarOpen(true)}
                  onClose={() => setInitialCalendarOpen(false)}
                  label="Date of Birth"
                  hasError={false}
                  labelId="date-of-birth"
                  isOpen={initialCalendarOpen}
                  // maxDate={getMaxDateOfBirth()}
                  disableOpenPicker={false}
                  disableFuture
                  slotProps={{
                    field: {
                      onKeyDown: (event) => event.stopPropagation(),
                      onBlur: () => onFieldBlur("dateOfBirth"),
                    },
                    textField: {
                      placeholder: "Select a date",
                      type: "text",
                      onKeyDown: (event) => event.preventDefault(),
                      onClick: () => {
                        setInitialCalendarOpen(!initialCalendarOpen);
                      },
                      focused: false,
                    },
                  }}
                  showCalendarIcon={false}
                  validationMessage={formData.dateOfBirth as string}
                  format="DD/MM/YYYY"
                  onChange={(e: unknown) => onFieldChange("dateOfBirth", e)}
                  value={formData.dateOfBirth || null}
                />
                <div className="mt-1 ml-2 text-xs text-red-800">
                  <span className="break-words">
                    {formErrors.dateOfBirth.message}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-3 flex flex-col gap-y-7">
              <div>
                <TextFieldComponent
                  id="emailAddress"
                  label="Email address (optional)"
                  labelId="emailLabel"
                  type="text"
                  placeholder="Enter email address"
                  value={formData.emailAddress || ""}
                  hasError={false}
                  onChange={(e) => onFieldChange("emailAddress", e)}
                  onBlur={() => onFieldBlur("emailAddress")}
                />

                <div className="mt-1 ml-2 text-xs text-red-800">
                  <span className="break-words">
                    {formErrors.emailAddress.message}
                  </span>
                </div>
              </div>
            </div>

            <div className=" flex gap-y-7">
              <div className="mt-3 flex flex-col gap-y-7">
                <h1 className="mb-[-20px] pl-2 text-sm text-neutral-500">
                  Country
                </h1>
                <div className="w-22 mt-[-2px] flex rounded-xl border-1 border-solid border-neutral-100 bg-neutral-25 p-2.5">
                  <FlagNigeria />
                  <h1 className="text-neutral-500">+234</h1>
                </div>
              </div>

              <div className="ml-4 mt-3 flex w-full flex-col">
                <TextFieldComponent
                  id="phoneNumber"
                  label="Phone Number"
                  labelId="phoneNumber"
                  type="text"
                  placeholder="Enter phone number"
                  value={formData.phoneNumber || ""}
                  hasError={false}
                  onChange={(e) => onFieldChange("phoneNumber", e)}
                  onBlur={() => onFieldBlur("phoneNumber")}
                  className="w-5/6 xsm:w-full"
                  maxLength={getPhoneNumberLength(
                    DEFAULT_COUNTRY_CODE,
                    formData[MerchantProfileFields.PHONE_NUMBER] || ""
                  )}
                />

                <div className="ml-2 mt-1 text-xs text-red-800">
                  <span className="break-words">
                    {formErrors.phoneNumber.message}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-3 flex flex-col gap-y-7">
              <div>
                <SelectComponent
                  items={relationshipItems}
                  className="w-full rounded-xl"
                  label="Relationship"
                  id="relationship-label"
                  labelId="relationship-label"
                  defaultValue={formData.relationship || ""}
                  placeholder="Select relationship"
                  hasError={selectedRelationshipError.hasError}
                  errorMessage={selectedRelationshipError.message}
                  onChange={(e, newValue) =>
                    onFieldChange("relationship", newValue, relationshipItems)
                  }
                  onClose={onSelectBlur}
                  editable={true}
                />
                <div className="mt-1 ml-2 text-xs text-red-800">
                  <span className="break-words">
                    {formErrors.relationship.message}
                  </span>
                </div>
              </div>
            </div>

            <div className="stepper-buttons-container">
              <button
                id="nin-back-button"
                className="secondary-button"
                onClick={handleModalState}
                type="button"
              >
                Discard
              </button>

              <button
                id="nin-continue-button"
                className="primary-button"
                type="submit"
                disabled={!isFormValid() || isContinueDisabled}
              >
                Continue
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="mt-6 flex flex-col">
          <h1 className="mb-16 text-4.5xl font-medium text-neutral-900">
            Next Of Kin Details
          </h1>
          <StepStatusComponent
            status={TierStepsResponseDTO.stepStatus.APPROVED}
            title={"Information Successfully Verified"}
            subTitle={'Press "Continue" to proceed.'}
            icon={<CheckCircle transform="scale(1.70)" />}
            handleDiscardButton={handleModalState}
            handleContinueButton={goToNextOpenStep}
          />
        </div>
      )}
    </div>
  );
});

export default NextOfKinDetailsPage;
